import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/Account.css';

const Account = () => {
  const { currentUser, updateUserProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: currentUser?.firstName || '',
    lastName: currentUser?.lastName || '',
    email: currentUser?.email || '',
    phone: currentUser?.phone || currentUser?.phoneNumber || '',
    countryCode: currentUser?.countryCode || '+1',
    avatar: currentUser?.avatar || ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const countryCodes = [
    { code: '+1', country: 'US/CA', flag: '🇺🇸' },
    { code: '+44', country: 'UK', flag: '🇬🇧' },
    { code: '+33', country: 'FR', flag: '🇫🇷' },
    { code: '+49', country: 'DE', flag: '🇩🇪' },
    { code: '+39', country: 'IT', flag: '🇮🇹' },
    { code: '+34', country: 'ES', flag: '🇪🇸' },
    { code: '+61', country: 'AU', flag: '🇦🇺' },
    { code: '+81', country: 'JP', flag: '🇯🇵' },
    { code: '+86', country: 'CN', flag: '🇨🇳' },
    { code: '+91', country: 'IN', flag: '🇮🇳' }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      // Prepare the profile data to update
      const profileUpdates = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        phoneNumber: formData.phone, // For backward compatibility
        countryCode: formData.countryCode,
        avatar: formData.avatar
      };

      // Update the user profile in Firestore and local state
      const result = await updateUserProfile(profileUpdates);
      
      if (result.success) {
        setMessage('Profile updated successfully!');
        setIsEditing(false);
      } else {
        setMessage(result.error || 'Failed to update profile');
      }
    } catch (error) {
      setMessage('Failed to update profile. Please try again.');
    }
    
    setLoading(false);
  };

  const handleCancel = () => {
    setFormData({
      firstName: currentUser?.firstName || '',
      lastName: currentUser?.lastName || '',
      email: currentUser?.email || '',
      phone: currentUser?.phone || currentUser?.phoneNumber || '',
      countryCode: currentUser?.countryCode || '+1',
      avatar: currentUser?.avatar || ''
    });
    setIsEditing(false);
    setMessage('');
  };

  const getInitials = () => {
    if (currentUser?.firstName && currentUser?.lastName) {
      return `${currentUser.firstName[0]}${currentUser.lastName[0]}`.toUpperCase();
    }
    return currentUser?.email?.[0].toUpperCase() || 'DR';
  };

  // Debug log to check current user data
  console.log('Current user data:', {
    phone: currentUser?.phone,
    phoneNumber: currentUser?.phoneNumber,
    countryCode: currentUser?.countryCode,
    hasPhone: !!(currentUser?.phone || currentUser?.phoneNumber),
    fullUser: currentUser
  });

  return (
    <div className="account-container">
      <div className="account-header">
        <h2>Account Settings</h2>
        <p>Manage your profile information and preferences</p>
      </div>

      <div className="account-content">
        <div className="profile-card">
          <div className="profile-avatar-section">
            <div className="current-avatar">
              {formData.avatar ? (
                <img src={formData.avatar} alt="Profile" className="avatar-image" />
              ) : (
                <div className="avatar-placeholder">
                  {getInitials()}
                </div>
              )}
            </div>
            {isEditing && (
              <div className="avatar-upload">
                <input
                  type="url"
                  name="avatar"
                  value={formData.avatar}
                  onChange={handleChange}
                  placeholder="Enter avatar image URL"
                  className="avatar-input"
                />
                <p className="avatar-help">Enter a URL for your profile picture</p>
              </div>
            )}
          </div>

          <div className="profile-info">
            {!isEditing ? (
              <div className="view-mode">
                <div className="info-group">
                  <label>Full Name</label>
                  <p>{(currentUser?.firstName || currentUser?.lastName) 
                    ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() 
                    : 'Not specified'}
                  </p>
                </div>
                <div className="info-group">
                  <label>Email Address</label>
                  <p>{currentUser?.email}</p>
                </div>
                <div className="info-group">
                  <label>Phone Number</label>
                  <p>
                    {(currentUser?.phone || currentUser?.phoneNumber) ? 
                      `${currentUser?.countryCode || '+1'} ${currentUser?.phone || currentUser?.phoneNumber}` : 
                      'Not specified'
                    }
                  </p>
                </div>
                <div className="profile-actions">
                  <button 
                    onClick={() => setIsEditing(true)}
                    className="edit-btn"
                  >
                    Edit Profile
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="edit-mode">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <div className="phone-input-group">
                    <select
                      name="countryCode"
                      value={formData.countryCode}
                      onChange={handleChange}
                      className="country-select"
                    >
                      {countryCodes.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.flag} {country.code} {country.country}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Enter phone number"
                      className="phone-input"
                    />
                  </div>
                </div>

                {message && (
                  <div className={`message ${message.includes('success') ? 'success' : 'error'}`}>
                    {message}
                  </div>
                )}

                <div className="form-actions">
                  <button 
                    type="button" 
                    onClick={handleCancel}
                    className="cancel-btn"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="save-btn"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        <div className="account-stats">
          <div className="stat-item">
            <div className="stat-icon">👤</div>
            <div className="stat-content">
              <h4>Account Type</h4>
              <p>Clinician</p>
            </div>
          </div>
          <div className="stat-item">
            <div className="stat-icon">📅</div>
            <div className="stat-content">
              <h4>Member Since</h4>
              <p>January 2024</p>
            </div>
          </div>
          <div className="stat-item">
            <div className="stat-icon">🏥</div>
            <div className="stat-content">
              <h4>Department</h4>
              <p>General Medicine</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Account;
